// react
import { useContext } from 'react';

// context
import { ContextUserEntity } from '../../../../App.context';
import {
	ContextActivities,
	ContextLoans,
	ContextProject,
	ContextUserProjectPermissions,
} from '../Project.context';

// i18n
import { Trans, useTranslation } from 'react-i18next';

// router
import { Link as RouterLink } from 'react-router-dom';

// mui
import { Box, Container, Grid, Link } from '@mui/material';

// icons
import { Dashboard as IconOverview } from '@mui/icons-material';

// pages
import { Details } from './Details/Details';
import { Loans } from './Loans/Loans';

// ui
import { Activity } from '../../../ui/Activity/Activity';
import { Loading } from '../../../ui/Loading/Loading';
import { Tasks, TasksProps } from '../../../ui/Tasks/Tasks';

// v2 listing header
import { HeaderListingPage } from '../../../ui/HeaderListingPage/HeaderListingPage';

export const Overview = () => {
	// hooks
	const { t } = useTranslation();

	// context
	const { project } = useContext(ContextProject);
	const { isLoadingLoans, loans } = useContext(ContextLoans);
	const { isLoadingActivities } = useContext(ContextActivities);
	const { userEntity } = useContext(ContextUserEntity);
	const { userProjectPermissions } = useContext(ContextUserProjectPermissions);

	// vars
	const tasks: TasksProps['items'] = [];

	// define tasks
	if (project?.status === 'ENABLED') {
		// check if any loans are disabled
		for (let i = 0; i < loans.length; i++) {
			const loan = loans[i];

			if (loan.status === 'DISABLED') {
				tasks.push({
					content: (
						<Trans
							components={{ strong: <strong /> }}
							i18nKey="tasks.enableLoan"
							values={{ id: loan.code }}
						/>
					),
					type: 'loan',
				});
			}
		}

		if (userEntity?.type === 'PIU') {
			tasks.push(
				{
					content: (
						<Trans
							components={{
								a: <Link component={RouterLink} to={`/project/${project.idWbg}/entities`} />,
							}}
							i18nKey="tasks.addEntities"
						/>
					),
					type: 'entity',
				},
				{
					content: (
						<Trans
							components={{
								a: <Link component={RouterLink} to={`/project/${project.idWbg}/suppliers`} />,
							}}
							i18nKey="tasks.addSuppliers"
						/>
					),
					type: 'supplier',
				},
				{
					content: (
						<Trans
							components={{
								a: <Link component={RouterLink} to={`/project/${project.idWbg}/beneficiaries`} />,
							}}
							i18nKey="tasks.addBeneficiaries"
						/>
					),
					type: 'individual',
				},
				{
					content: (
						<Trans
							components={{
								a: <Link component={RouterLink} to={`/project/${project.idWbg}/accounts`} />,
							}}
							i18nKey="tasks.addLocalAccounts"
						/>
					),
					type: 'account',
				}
			);
		}
	}

	if (!project) {
		return <></>;
	}

	// Hold a full-page spinner until the overview's data has loaded so users
	// never see the header render above blank Loans/Tasks/Activity cards while
	// their fetches are still in flight.
	if (isLoadingLoans || isLoadingActivities) {
		return <Loading variant="page" />;
	}

	return (
		<>
			{(project?.version === '2') && (
				<HeaderListingPage
					icon={IconOverview}
					title={t('overview')}
					subtitle={t('overview.subtitle')}
					variant="activity"
				/>
			)}
			<Box sx={{ pt: (project?.version === '2') ? 3 : 0 }}>
			<Grid container spacing={2}>
				<Grid item xs={12}>
					<Details project={project} />
				</Grid>
				{userProjectPermissions?.loan?.read && (
					<Grid item xs={12}>
						<Loans project={project} />
					</Grid>
				)}
				<Grid item xs={12}>
					<Container maxWidth={false}>
						<Grid container spacing={2}>
							<Grid item xs={12} lg={6}>
								<Tasks items={tasks} />
							</Grid>
							<Grid item xs={12} lg={6}>
								<Activity project={project} />
							</Grid>
						</Grid>
					</Container>
				</Grid>
			</Grid>
			</Box>
		</>
	);
};